import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client.js";

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  unauthorized: boolean;
  reload: () => void;
}

export function useApi<T>(loader: () => Promise<T>, deps: unknown[], refreshKey: number): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  // loader identity changes each render; deps + refreshKey drive re-fetching intentionally.
  const run = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setUnauthorized(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) setUnauthorized(true);
        setError(err instanceof Error ? err.message : "unknown error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const cleanup = run();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, refreshKey]);

  return { data, loading, error, unauthorized, reload: run };
}
